import React from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { API, api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Send, Sparkles } from "lucide-react";

export default function VideoModal({ video, onClose, onCommissionRequest }) {
    const { user } = useAuth();
    const [msg, setMsg] = React.useState("");
    const [submitting, setSubmitting] = React.useState(false);

    if (!video) return null;

    const requestLicense = async () => {
        if (!user) {
            toast.error("Please log in as a brand to license videos");
            return;
        }
        if (user.role !== "brand" && user.role !== "admin") {
            toast.error("Only brand accounts can license videos");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/licenses", { video_id: video.id, message: msg });
            toast.success("License request sent to creator");
            setMsg("");
            onClose();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const canCommission = user && (user.role === "brand" || user.role === "admin");

    return (
        <Dialog open={!!video} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="max-w-4xl bg-white border-slate-200 rounded-2xl p-0 overflow-hidden"
                data-testid="video-modal"
            >
                <div className="aspect-video bg-black w-full">
                    <video
                        controls
                        className="w-full h-full"
                        src={`${API}/videos/${video.id}/file`}
                        data-testid="video-player"
                    />
                </div>
                <div className="p-6 space-y-4">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl font-semibold text-slate-900">
                            {video.title}
                        </DialogTitle>
                        <DialogDescription className="text-slate-500 text-sm">
                            by{" "}
                            <Link
                                to={`/creators/${video.creator_id}`}
                                onClick={onClose}
                                className="text-coral hover:underline font-medium"
                                data-testid="modal-creator-link"
                            >
                                {video.creator_name}
                            </Link>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-wrap gap-2 text-xs">
                        <span className="chip chip-lime">{video.category}</span>
                        <span className="chip chip-neutral">{video.vertical}</span>
                        {video.tags?.slice(0, 4).map((t) => (
                            <span key={t} className="chip">#{t}</span>
                        ))}
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">
                        {video.description || "No description"}
                    </p>

                    {canCommission && (
                        <div className="border-t hairline pt-4 space-y-3">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <label className="overline text-slate-500">License this video</label>
                                {onCommissionRequest && (
                                    <button
                                        onClick={() => {
                                            onCommissionRequest(video);
                                            onClose();
                                        }}
                                        className="btn-pill-lime inline-flex items-center gap-2"
                                        data-testid="modal-commission-button"
                                    >
                                        <Sparkles className="w-4 h-4" />
                                        Commission a custom video
                                    </button>
                                )}
                            </div>
                            <textarea
                                value={msg}
                                onChange={(e) => setMsg(e.target.value)}
                                placeholder="Tell the creator about your campaign…"
                                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-900 focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/20"
                                rows={3}
                                data-testid="license-message"
                            />
                            <button
                                onClick={requestLicense}
                                disabled={submitting}
                                className="btn-pill-primary inline-flex items-center"
                                data-testid="license-request-button"
                            >
                                <Send className="w-4 h-4 mr-2" />
                                {submitting ? "Sending…" : "Request license"}
                            </button>
                        </div>
                    )}
                    {!user && (
                        <div className="border-t hairline pt-4">
                            <Link to="/register">
                                <button className="btn-pill-primary" data-testid="modal-signup-cta">
                                    Sign up as a brand to license →
                                </button>
                            </Link>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
